import {
  assertNonEmptyString,
  assertOwn,
  assertRecord,
  validateAction,
} from '../elements/elementFields.validation';
import type { FmSectionState } from './section.types';

type StateRecord = Record<string, unknown>;

type StateValidator = (record: StateRecord, id: string) => void;

const STATE_KEYS: Record<FmSectionState['status'], readonly string[]> = {
  ready: ['status'],
  loading: ['status', 'label'],
  empty: ['status', 'label', 'explanation', 'action'],
  partial: ['status', 'label', 'explanation', 'missingFieldsLabel', 'missingFields'],
  stale: [
    'status',
    'label',
    'explanation',
    'observedAtLabel',
    'observedAt',
    'refreshLabel',
    'onRefresh',
  ],
  locked: ['status', 'label', 'explanation', 'action'],
  error: ['status', 'label', 'explanation', 'retryLabel', 'onRetry'],
};

function validateUnavailableStateCopy(record: StateRecord, id: string): void {
  assertNonEmptyString(record.label, `state.label for "${id}"`);
  assertNonEmptyString(record.explanation, `state.explanation for "${id}"`);
}

const validatePartialState: StateValidator = (record, id) => {
  validateUnavailableStateCopy(record, id);
  assertNonEmptyString(record.missingFieldsLabel, `state.missingFieldsLabel for "${id}"`);
  if (!Array.isArray(record.missingFields) || record.missingFields.length === 0) {
    throw new Error(`Invalid descriptor "${id}": partial state requires missingFields.`);
  }
  record.missingFields.forEach((field, index) => {
    assertNonEmptyString(field, `state.missingFields[${index}] for "${id}"`);
  });
};

const validateStaleState: StateValidator = (record, id) => {
  validateUnavailableStateCopy(record, id);
  assertNonEmptyString(record.observedAtLabel, `state.observedAtLabel for "${id}"`);
  assertNonEmptyString(record.observedAt, `state.observedAt for "${id}"`);
  const hasRefresh = record.onRefresh !== undefined || record.refreshLabel !== undefined;
  if (hasRefresh && typeof record.onRefresh !== 'function') {
    throw new Error(`Invalid descriptor "${id}": state.onRefresh must be a function.`);
  }
  if (hasRefresh) assertNonEmptyString(record.refreshLabel, `state.refreshLabel for "${id}"`);
};

const validateLockedState: StateValidator = (record, id) => {
  validateUnavailableStateCopy(record, id);
  if (record.action === undefined) return;
  validateAction(record.action, `state.action for "${id}"`);
};

const validateErrorState: StateValidator = (record, id) => {
  validateUnavailableStateCopy(record, id);
  const hasRetry = record.onRetry !== undefined || record.retryLabel !== undefined;
  if (hasRetry && typeof record.onRetry !== 'function') {
    throw new Error(`Invalid descriptor "${id}": state.onRetry must be a function.`);
  }
  if (hasRetry) assertNonEmptyString(record.retryLabel, `state.retryLabel for "${id}"`);
};

const STATE_VALIDATORS: Record<FmSectionState['status'], StateValidator> = {
  ready: () => undefined,
  loading: (record, id) => assertNonEmptyString(record.label, `state.label for "${id}"`),
  // An empty state's optional action mirrors locked's: one owned next step.
  empty: validateLockedState,
  partial: validatePartialState,
  stale: validateStaleState,
  locked: validateLockedState,
  error: validateErrorState,
};

export function validateState(state: unknown, id: string): asserts state is FmSectionState {
  assertRecord(state, `state for "${id}"`);
  const record = state;
  assertOwn(record, 'status', `state for "${id}"`);
  const status = record.status as FmSectionState['status'];
  if (
    typeof status !== 'string' ||
    !Object.prototype.hasOwnProperty.call(STATE_VALIDATORS, status)
  ) {
    throw new Error(`Invalid descriptor "${id}": unknown state "${String(record.status)}".`);
  }
  const allowed = new Set(STATE_KEYS[status]);
  const unknownKey = Object.keys(record).find((key) => !allowed.has(key));
  if (unknownKey) {
    throw new Error(
      `Invalid descriptor "${id}": unknown state key "${unknownKey}" for "${status}".`,
    );
  }
  STATE_VALIDATORS[status](record, id);
}
